// src/pages/HousekeepingScreen.js

import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
    UserCheck, Clock, AlertCircle, Eye, Building, ListChecks, CheckCheck, 
    UserPlus, BarChart3, LayoutDashboard, Search, ChevronRight, Users, RefreshCw 
} from 'lucide-react';
import { api } from '../api';
import toast from 'react-hot-toast';
import '../styles/HousekeepingScreen.css'; 
import HousekeepingAnalytics from '../components/housekeeping/HousekeepingAnalytics';

const HousekeepingScreen = () => {
    // --- View State ---
    const [activeTab, setActiveTab] = useState('board'); 
    
    // --- Filter State ---
    const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
    
    // --- Mobile State ---
    const [mobileCol, setMobileCol] = useState('NO_TASK'); 
    const [searchTerm, setSearchTerm] = useState(''); 

    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState('');
    const [loading, setLoading] = useState(true);
    
    const [housekeepers, setHousekeepers] = useState([]); // Working Today
    const [allStaff, setAllStaff] = useState([]); // Full Roster
    const [boardRooms, setBoardRooms] = useState([]);

    // --- Load Properties ---
    useEffect(() => {
        const loadProperties = async () => {
            try {
                const props = await api.getProperties(); 
                if (props && props.length > 0) {
                    setProperties(props);
                    setSelectedPropertyId(props[0].id);
                }
            } catch (err) {
                toast.error("Could not load properties");
            }
        };
        loadProperties();
    }, []);

    // --- Data Loading Function (Reusable) ---
    const loadBoardData = useCallback(async () => {
        if (!selectedPropertyId) return;
        setLoading(true);
        try {
            // 1. Fetch Board (Rooms + Working Staff)
            const data = await api.getHousekeepingBoard(selectedPropertyId);
            setHousekeepers(data.housekeepers || []); 
            setBoardRooms(data.rooms || []);

            // 2. Fetch Full Roster (For Filter)
            const users = await api.getWorkforce({ propertyId: selectedPropertyId });
            const staff = users.filter(u => u.role === 'Housekeeper' || u.role === 'Manager');
            setAllStaff(staff);

        } catch (error) {
            console.error(error);
            toast.error("Failed to refresh board");
        } finally {
            setLoading(false);
        }
    }, [selectedPropertyId]);

    // --- Initial Load Effect ---
    useEffect(() => {
        if (activeTab === 'board' && selectedPropertyId) {
            loadBoardData();
        }
    }, [activeTab, selectedPropertyId, loadBoardData]);

    // --- Refresh / Reset Handler ---
    const handleRefresh = async () => {
        setSelectedEmployeeId(''); // Reset Employee Filter
        setSearchTerm('');         // Reset Search
        await loadBoardData();     // Re-fetch Data
        toast.success("View reset & refreshed");
    };

    // --- AUTO-SWITCH TAB LOGIC ---
    useEffect(() => {
        if (!searchTerm) return;
        const match = boardRooms.find(r => 
            r.room_number.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (r.assignee_name && r.assignee_name.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        if (match) {
            setMobileCol(match.board_status);
        }
    }, [searchTerm, boardRooms]);

    // --- FILTER LOGIC ---
    const getByStatus = (status) => {
        let rooms = boardRooms.filter(r => r.board_status === status);

        if (selectedEmployeeId) {
            rooms = rooms.filter(r => r.assignee_id === parseInt(selectedEmployeeId));
        }

        if (searchTerm) {
            rooms = rooms.filter(r => 
                r.room_number.toLowerCase().includes(searchTerm.toLowerCase()) || 
                (r.assignee_name && r.assignee_name.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }
        return rooms;
    };

    // --- Helpers (Update, Drag, etc.) ---
    const updateStatus = async (room, newStatus, assigneeId = null) => {
        const previousState = [...boardRooms];
        const updatedRooms = boardRooms.map(r => 
            r.room_id === room.room_id 
            ? { ...r, board_status: newStatus, assignee_id: assigneeId } 
            : r
        );
        setBoardRooms(updatedRooms);

        try {
            await api.updateTaskStatus({
                taskId: room.task_id,
                roomId: room.room_id,
                propertyId: selectedPropertyId,
                status: newStatus,
                assignedUserId: assigneeId
            });
            
            let message = `Moved Room ${room.room_number}`;
            const hk = housekeepers.find(h => h.id == assigneeId);
            const hkName = hk ? hk.first_name : 'Staff';

            if (newStatus === 'IN_PROGRESS') message = `Room ${room.room_number} started by ${hkName}`;
            else if (newStatus === 'COMPLETED') message = `Room ${room.room_number} is Clean`;
            
            if (newStatus !== 'NO_TASK') toast.success(message);
            
            // Background refresh to keep sync
            const data = await api.getHousekeepingBoard(selectedPropertyId);
            setBoardRooms(data.rooms);
        } catch (err) {
            console.error(err);
            toast.error("Action failed");
            setBoardRooms(previousState);
        }
    };

    const handleAssign = (room, userId) => {
        updateStatus(room, room.board_status, userId);
    };

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;

        const roomId = parseInt(draggableId);
        const room = boardRooms.find(r => r.room_id === roomId);
        const newColumn = destination.droppableId; 

        if (!room) return;

        if (newColumn === 'IN_PROGRESS' && !room.assignee_id) {
            toast.error("Assign staff first");
            return; 
        }
        updateStatus(room, newColumn, room.assignee_id);
    };

    // --- Mobile Quick Actions ---
    const renderMobileAction = (room, currentColumn) => {
        switch(currentColumn) {
            case 'NO_TASK':
                return <button className="hk-mobile-action-btn primary" onClick={() => updateStatus(room, 'PENDING')}>Add to Queue <ChevronRight size={14} /></button>;
            case 'PENDING':
                return <button className="hk-mobile-action-btn primary" onClick={() => { if(!room.assignee_id) toast.error("Assign staff first"); else updateStatus(room, 'IN_PROGRESS', room.assignee_id); }}>Start <ChevronRight size={14} /></button>;
            case 'IN_PROGRESS':
                return <button className="hk-mobile-action-btn success" onClick={() => updateStatus(room, 'PENDING_INSPECTION', room.assignee_id)}>Finish <ChevronRight size={14} /></button>;
            case 'PENDING_INSPECTION':
                return <button className="hk-mobile-action-btn success" onClick={() => updateStatus(room, 'COMPLETED')}>Approve <CheckCheck size={14} /></button>;
            default: return null;
        }
    };

    // --- Render Column ---
    const renderColumn = (columnId, title, icon, colorClass, roomsInColumn) => {
        const isCompact = columnId === 'NO_TASK';
        return (
            <div className={`hk-column ${isCompact ? 'hk-column-compact' : ''} ${mobileCol === columnId ? 'mobile-visible' : 'mobile-hidden'}`}>
                <div className={`hk-col-header ${colorClass}`}>
                    {icon} <span>{title}</span> <span className="count-badge">{roomsInColumn.length}</span>
                </div>
                <Droppable droppableId={columnId}>
                    {(provided, snapshot) => (
                        <div className={`hk-col-body ${snapshot.isDraggingOver ? 'dragging-over' : ''}`} ref={provided.innerRef} {...provided.droppableProps}>
                            {roomsInColumn.map((room, index) => (
                                <Draggable key={room.room_id} draggableId={String(room.room_id)} index={index}>
                                    {(provided, snapshot) => (
                                        <div
                                            className={`hk-card ${isCompact ? 'hk-card-compact' : ''} ${snapshot.isDragging ? 'is-dragging' : ''}`}
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                ...provided.draggableProps.style,
                                                borderLeft: `4px solid ${
                                                    columnId === 'PENDING' ? '#ef4444' :
                                                    columnId === 'IN_PROGRESS' ? '#f59e0b' :
                                                    columnId === 'PENDING_INSPECTION' ? '#3b82f6' : 
                                                    columnId === 'COMPLETED' ? '#10b981' : '#cbd5e1'
                                                }`
                                            }}
                                        >
                                            {isCompact ? (
                                                <div className="hk-card-content-compact">
                                                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                                        <div className="hk-room-num-small">{room.room_number}</div>
                                                        <div className="hk-compact-details">
                                                            {room.floor_name && <span className="hk-room-floor">{room.floor_name}</span>}
                                                            <span className="hk-room-type-small">{room.room_type}</span>
                                                        </div>
                                                    </div>
                                                    <div className="mobile-only-action">{renderMobileAction(room, columnId)}</div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="hk-card-top">
                                                        <span className="hk-room-num">{room.room_number}</span>
                                                        <span className="hk-room-type">{room.room_type}</span>
                                                    </div>
                                                    <div className="hk-card-footer" onClick={e => e.stopPropagation()}>
                                                        {room.assignee_name ? (
                                                            <div className="hk-assignee"><UserCheck size={12} /> {room.assignee_name}</div>
                                                        ) : (columnId === 'IN_PROGRESS' || columnId === 'PENDING') ? (
                                                            <div className="hk-assign-wrapper">
                                                                <UserPlus size={14} className="hk-assign-icon"/>
                                                                <select className="hk-assign-select" value="" onChange={(e) => handleAssign(room, e.target.value)}>
                                                                    <option value="" disabled>Assign Staff...</option>
                                                                    {housekeepers.map(hk => <option key={hk.id} value={hk.id}>{hk.first_name} {hk.last_name}</option>)}
                                                                </select>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    <div className="mobile-only-action full-width-btn">{renderMobileAction(room, columnId)}</div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </div>
        );
    };

    const columns = [
        { id: 'NO_TASK', label: 'Daily List', count: getByStatus('NO_TASK').length },
        { id: 'PENDING', label: 'Queue', count: getByStatus('PENDING').length },
        { id: 'IN_PROGRESS', label: 'Active', count: getByStatus('IN_PROGRESS').length },
        { id: 'PENDING_INSPECTION', label: 'Inspect', count: getByStatus('PENDING_INSPECTION').length },
        { id: 'COMPLETED', label: 'Done', count: getByStatus('COMPLETED').length },
    ];

    return (
        <div className="housekeeping-container">
            {/* Header */}
            <div className="page-header">
                <h1 className="page-title">Housekeeping</h1>
                <div className="page-header-actions">
                    <div className="hk-view-tabs">
                        <button className={`hk-tab-btn ${activeTab === 'board' ? 'active' : ''}`} onClick={() => setActiveTab('board')}>
                            <LayoutDashboard size={18} /> <span className="desktop-only">Board</span>
                        </button>
                        <button className={`hk-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>
                            <BarChart3 size={18} /> <span className="desktop-only">Stats</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="hk-filter-container">
                {/* 1. Property Dropdown - ALWAYS VISIBLE */}
                <div className="hk-property-selector">
                    <Building size={14} style={{color:'#64748b'}}/>
                    <select 
                        value={selectedPropertyId} 
                        onChange={(e) => setSelectedPropertyId(e.target.value)}
                        className="property-select-dropdown"
                    >
                        {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>

                {/* --- BOARD SPECIFIC FILTERS --- */}
                {/* Only show these if we are on the BOARD tab. 
                    This removes clutter/duplication when viewing Analytics. */}
                {activeTab === 'board' && (
                    <>
                        {/* 2. Employee Filter */}
                        <div className="hk-property-selector">
                            <Users size={14} style={{color:'#64748b'}}/>
                            <select 
                                value={selectedEmployeeId} 
                                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                                className="property-select-dropdown"
                            >
                                <option value="">All Employees</option>
                                {allStaff.map(hk => (
                                    <option key={hk.id} value={hk.id}>{hk.first_name} {hk.last_name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 3. Staff Active Badge */}
                        <div className="hk-stat-badge">
                            <UserCheck size={16} className="text-blue" />
                            <span>{housekeepers.length} Active</span>
                        </div>

                        {/* 4. Refresh Button */}
                        <button 
                            className="hk-refresh-btn" 
                            onClick={handleRefresh}
                            title="Reset Filters & Refresh"
                        >
                            <RefreshCw size={18} />
                        </button>
                    </>
                )}
            </div>

            {loading ? <div>Loading...</div> : (
                <>
                    {activeTab === 'board' && (
                        <div className="hk-board-view">
                            <div className="hk-mobile-controls">
                                <div className="hk-mobile-stats">
                                    <div className="hk-stat-pill">
                                        <UserCheck size={14} className="text-blue" />
                                        <span>{housekeepers.length} Staff</span>
                                    </div>
                                    <div className="hk-search-bar">
                                        <Search size={14} color="#64748b"/>
                                        <input placeholder="Search room..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                                    </div>
                                </div>
                                <div className="hk-mobile-tabs">
                                    {columns.map(col => (
                                        <button key={col.id} className={`hk-mobile-tab tab-${col.id} ${mobileCol === col.id ? 'active' : ''}`} onClick={() => setMobileCol(col.id)}>
                                            {col.label} <span className="mobile-badge">{col.count}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <DragDropContext onDragEnd={onDragEnd}>
                                <div className="hk-board full-width">
                                    {renderColumn('NO_TASK', 'Daily Room List', <ListChecks size={16} />, 'header-idle', getByStatus('NO_TASK'))}
                                    {renderColumn('PENDING', 'To Clean / Queue', <AlertCircle size={16} />, 'header-todo', getByStatus('PENDING'))}
                                    {renderColumn('IN_PROGRESS', 'In Progress', <Clock size={16} />, 'header-progress', getByStatus('IN_PROGRESS'))}
                                    {renderColumn('PENDING_INSPECTION', 'Needs Inspection', <Eye size={16} />, 'header-inspect', getByStatus('PENDING_INSPECTION'))}
                                    {renderColumn('COMPLETED', 'Clean / Ready', <CheckCheck size={16} />, 'header-ready', getByStatus('COMPLETED'))}
                                </div>
                            </DragDropContext>
                        </div>
                    )}

                    {activeTab === 'analytics' && (
                        <div className="hk-analytics-view">
                            <HousekeepingAnalytics 
                                propertyId={selectedPropertyId} 
                                // We do NOT pass selectedEmployeeId here anymore, 
                                // because Analytics has its own internal filter.
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default HousekeepingScreen;